import { diffChars } from 'diff';
import UniversalTag from './UniversalTag';

/**
 * Render a cell value as text.
 *
 * Record data is JSON - `Record<string, unknown>` - so a field is whatever type the source gave
 * it, and nr_students arrives as a number. diffChars only understands strings and silently
 * returns [] for anything else, which rendered a blank cell for a field that did have a value.
 * Coercing here keeps the diff and the plain render on strings. `0` and `false` become "0" and
 * "false" rather than being mistaken for absent values.
 */
function toText(value: unknown): string {
  return value == null ? '' : String(value);
}

// Single-column diff value component for side-by-side layouts (used in RecordExplanationSlideout)
interface DiffValueCellProps {
  value: unknown;
  compareValue: unknown;
  type: 'old' | 'new';
  isChanged: boolean;
}

export function DiffValueCell({ value, compareValue, type, isChanged }: DiffValueCellProps) {
  const text = toText(value);

  if (!isChanged) {
    return <span className="export-review-grid__cell-value mono-1">{text || <UniversalTag category="Empty" />}</span>;
  }

  const oldVal = type === 'old' ? text : toText(compareValue);
  const newVal = type === 'new' ? text : toText(compareValue);

  if (!oldVal && !newVal) {
    return <UniversalTag category="Empty" />;
  }

  const changes = diffChars(oldVal, newVal);

  if (type === 'old') {
    // Build old line: show unchanged + removed (skip added)
    const content = changes.map((part, idx) => {
      if (part.added) return null;
      if (part.removed) {
        return <span key={idx} className="export-review-grid__diff-removed">{part.value}</span>;
      }
      return <span key={idx}>{part.value}</span>;
    });
    return <span className="export-review-grid__cell-value mono-1">{text ? content : <UniversalTag category="Empty" />}</span>;
  } else {
    // Build new line: show unchanged + added (skip removed)
    const content = changes.map((part, idx) => {
      if (part.removed) return null;
      if (part.added) {
        return <span key={idx} className="export-review-grid__diff-added">{part.value}</span>;
      }
      return <span key={idx}>{part.value}</span>;
    });
    return <span className="export-review-grid__cell-value mono-1">{text ? content : <UniversalTag category="Empty" />}</span>;
  }
}

interface ExportReviewGridCellProps {
  fieldName: string;
  value: unknown;
  targetValue: unknown;
  isChanged: boolean;
  showChanges: boolean;
  isUpdateOperation: boolean;
}

export function ExportReviewGridCell({
  fieldName,
  value,
  targetValue,
  isChanged,
  showChanges,
  isUpdateOperation
}: ExportReviewGridCellProps) {
  // For UPDATE operations with changes, show diff when showChanges is true
  if (isUpdateOperation && isChanged && showChanges) {
    const oldVal = toText(targetValue);
    const newVal = toText(value);

    if (!oldVal && !newVal) {
      return <UniversalTag category="Empty" />;
    }

    const changes = diffChars(oldVal, newVal);

    // Build single line: show removed (strikethrough) + unchanged + added (green)
    const diffLine = changes.map((part, idx) => {
      if (part.removed) {
        return <span key={idx} className="export-review-grid__diff-removed">{part.value}</span>;
      }
      if (part.added) {
        return <span key={idx} className="export-review-grid__diff-added">{part.value}</span>;
      }
      return <span key={idx}>{part.value}</span>;
    });

    const tooltip = `"${fieldName}" will change from "${oldVal || '(empty)'}" to "${newVal || '(empty)'}"`;

    return (
      <span
        className="export-review-grid__cell-value mono-1"
        data-tooltip-id="main-tooltip"
        data-tooltip-content={tooltip}
        data-tooltip-place="top-start"
        data-tooltip-position-strategy="fixed"
      >
        {newVal ? diffLine : <UniversalTag category="Empty" />}
      </span>
    );
  }

  return <span className="export-review-grid__cell-value mono-1">{toText(value) || <UniversalTag category="Empty" />}</span>;
}
